import React, { useState } from "react";
import SearchProps from "@vahak/core-ui/dist/components/Search/Search.types";
import {
    CSUserData,
    filterHiddenCsUser,
    GetCSUsersParams,
    useGetCSUsers,
    useGetCSUsersMutation
} from "../../../services/admin-user/useGetCSUsers";
import Search from "@vahak/core-ui/dist/components/Search";
import { generateSelectOption } from "../../../methods/uiUtil";
import { formatPhoneNumber } from "../../../methods/formatPhoneNumber";
import { OnChangeSelectHandlerType, SelectOnChangeType, SelectOption } from "@vahak/core-ui/dist/components/Select";
import { stringOperations } from "@vahak/core-ui/dist/methods/string";
import { CSSObject } from "styled-components";
import { NoSuccessErrorHandler } from "@vahak/core/dist/constants/api";
import Chip from "@vahak/core-ui/dist/components/Chip";
import { useCsDashboard } from "@vahak/core/dist/hooks";

enum CsAttendanceAction {
    LOG_OUT = 1,
    LOG_IN = 2,
    BREAK = 3
}

interface SearchCsUserOptionProps {
    searchProps?: Partial<SearchProps>;
    wrapperClassName?: string;
    filter?: GetCSUsersParams;
    inlineStyle?: CSSObject;
    onSelect?: (user?: CSUserData) => void;
    selectedUser?: CSUserData;
}

const SearchCsUserOption = ({
    searchProps,
    wrapperClassName,
    inlineStyle,
    filter,
    onSelect,
    selectedUser
}: SearchCsUserOptionProps) => {
    const [phoneNumberText, setPhoneNumberText] = useState("");
    const { isDeveloper } = useCsDashboard();
    const getUserLabel = (user: CSUserData) => {
        const phone = user.phone_number ? `+91-${formatPhoneNumber(user.phone_number.replace("+91", ""))}` : "";
        return [user.name, isDeveloper && phone ? `(${phone})` : ""].filter(Boolean).join(" ");
    };
    const selectedOption = selectedUser ? generateSelectOption(getUserLabel(selectedUser), selectedUser) : undefined;

    const { mutateAsync: getCsUserList } = useGetCSUsersMutation();

    const getCsUserListOption = (list: CSUserData[] = []) => {
        return list.map(
            (user) =>
                ({
                    ...generateSelectOption(getUserLabel(user), user),
                    leftNode: (
                        <Chip
                            color={user?.current_attendance_status === CsAttendanceAction.LOG_IN ? "green" : "grey"}
                            filled
                            padded={false}
                            radius={32}
                            size="dot"
                            dotSize={2}
                        />
                    )
                } as SelectOption)
        );
    };

    const { isFetching, hasNextPage, fetchNextPage, data } = useGetCSUsers(
        {
            ...filter
        },
        {
            ...NoSuccessErrorHandler,
            refetchOnWindowFocus: false,
            cacheTime: Infinity,
            staleTime: Infinity
        }
    );
    const users = getCsUserListOption(data?.pages?.flatMap((p) => p?.data?.details));

    const onChangeSelectInput: OnChangeSelectHandlerType = (newValue) => {
        onSelect?.(newValue?.value);
    };

    const getPhoneNumberFroText = (str: string) => {
        return stringOperations(str, ["removeNonNumeric", "trim", "truncate:10"]);
    };

    const onChangeSearchInput = (str: string) => {
        setPhoneNumberText(getPhoneNumberFroText(str));
    };

    return (
        <span className={wrapperClassName} style={inlineStyle}>
            <Search
                loadOptions={async (input, cb) => {
                    const phoneNumber = getPhoneNumberFroText(input);
                    if (phoneNumber?.length === 10) {
                        const data = await getCsUserList({ phoneNumber });
                        cb(getCsUserListOption(filterHiddenCsUser(data?.data?.details ?? [], !!isDeveloper)));
                    } else {
                        cb([]);
                    }
                }}
                isLoading={isFetching}
                inputValue={phoneNumberText}
                onInputChange={onChangeSearchInput}
                value={selectedOption}
                defaultOptions={users}
                onChange={onChangeSelectInput as SelectOnChangeType}
                option={[]}
                placeholder="Select User"
                onMenuScrollToBottom={() => {
                    hasNextPage && fetchNextPage();
                }}
                noOptionText="No Users"
                isClearable
                required
                {...searchProps}
            />
        </span>
    );
};

export default SearchCsUserOption;
